import React from 'react';
import { get } from 'lodash-es';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { updateVehicle } from 'client/actions/vehicle';
import { TypePageDecorator } from 'site-modules/upper-funnel/decorators/type-page-decorator';
import { defineSeoContent } from 'site-modules/upper-funnel/utils/seo-content';
import {
  buildSubTypeRankingsPath,
  buildSubTypeRankingsPathForAboveTheFold,
  buildSubTypeRankingsPathForBelowTheFold,
  TypeRankingsModel,
} from 'client/data/models/type-rankings';
import { SuggestedVehicleModel } from 'client/data/models/suggested-vehicle';
import { setCriticalCss } from 'client/actions/critical-css';
import { hasAnchorNavForSubtypePage } from 'site-modules/shared/utils/upper-funnel/type-rankings';
import { getSubtypeContentPath } from 'site-modules/shared/utils/upper-funnel/content-path';
import { PageModel } from 'client/data/models/page';
import { CmsModel } from 'client/data/models/cms';
import { HomeModulesModel } from 'client/data/models/home-modules';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { SEGMENT_LISTING_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import {
  getCreativeConfigAdPathByVehicle,
  setAdsListToPreload,
  siteServedAdsCriticalCss,
} from 'site-modules/shared/components/native-ad/utils/utils';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { TypeLeaseDealsModel, TypeLeaseDealsPaths } from 'client/data/models/type-lease-deals';
import { buildLeaseDealsHiddenGemsCriticalCss } from 'site-modules/upper-funnel/utils/lease-deals-hidden-gems-critical-css';
import { getMostSoldTypeContentPath } from 'site-modules/shared/utils/upper-funnel/most-sold-type-infographic';
import { getBestRankingsForAboveTheFold, getBestRankingsForBelowTheFold, SEOModel } from 'client/data/models/seo';
import { getIsSeot4733 } from 'client/utils/seot-4733';
import { HERO_BG_PATHS } from 'site-modules/upper-funnel/components/type/hero-content/constants';

import { SubtypeRenderer } from './subtype-renderer';

function preload(store, type, subtype, isBelowFoldPreload) {
  const { mobile, featureFlags } = store.getState();
  const preloader = new ModelPreloader(store);

  const rankingsPath = buildSubTypeRankingsPath(`${type},${subtype}`, subtype);
  const isSeot4733 = getIsSeot4733(get(featureFlags, 'seot-4733-level-2-subtype-pages'), type, subtype);
  if (isSeot4733) {
    preloader.resolve(getBestRankingsForAboveTheFold(), SEOModel);
  } else {
    preloader.resolve(buildSubTypeRankingsPathForAboveTheFold(`${type},${subtype}`, subtype), TypeRankingsModel);
  }
  preloader.resolve(TypeLeaseDealsPaths.getTypeLeaseDealsTypeCategoryPath(type), TypeLeaseDealsModel);

  const vehicle = { type: { niceName: type } };
  store.dispatch(updateVehicle(vehicle));
  setAdsListToPreload(preloader, store.getState(), [SEGMENT_LISTING_AD.AD_NAME], vehicle);

  if (isBelowFoldPreload) {
    if (isSeot4733) {
      preloader.resolve(getBestRankingsForBelowTheFold(), SEOModel);
    } else {
      preloader.resolve(buildSubTypeRankingsPathForBelowTheFold(`${type},${subtype}`, subtype), TypeRankingsModel);
    }

    preloader.resolve('vehicleShowcaseContent', HomeModulesModel);
    const mostSoldTypeUniqueName = `${subtype}-${type}s`.toLowerCase();
    preloader.resolve(`content["${getMostSoldTypeContentPath(mostSoldTypeUniqueName)}"]`, CmsModel);
    preloader.resolve(`content["${getSubtypeContentPath(type, subtype)}"]`, CmsModel);
  }

  return preloader.load().then(modelState => {
    const vehicleEditorialRankings = isSeot4733
      ? get(modelState.get('bestRankings', SEOModel), 'rankings', [])
      : modelState.get(rankingsPath, TypeRankingsModel);
    const siteServedAds = modelState.get(
      getCreativeConfigAdPathByVehicle(mobile, vehicle),
      NativeAdsCreativeConfigModel
    );
    const leaseDeals = modelState.get(TypeLeaseDealsPaths.getTypeLeaseDealsTypeCategoryPath(type), TypeLeaseDealsModel);

    const firstVehicle = vehicleEditorialRankings?.[0]?.segmentRatings?.[0]?.modelYear;
    if (firstVehicle) {
      preloader.set('forcedVehicleParams', SuggestedVehicleModel, {
        make: firstVehicle.makeSlug,
        model: firstVehicle.modelSlug,
        year: firstVehicle.year,
      });
    }
    const imageHref = mobile ? HERO_BG_PATHS.sm : HERO_BG_PATHS.lg;
    const assetsToPreload = [{ href: imageHref, as: 'image' }];
    preloader.set('server.preloadedAssets', PageModel, assetsToPreload);
    store.dispatch(
      setCriticalCss({
        type,
        criticalCssHeight: 2000,
        hasAnchorNav: hasAnchorNavForSubtypePage(type, subtype, vehicleEditorialRankings),
        ...siteServedAdsCriticalCss(siteServedAds),
        topRatedPromo: !!modelState.get('top-rated-promo', FeatureFlagModel),
        ...buildLeaseDealsHiddenGemsCriticalCss({ vehicleEditorialRankings, leaseDeals }),
      })
    );
    return preloader.load();
  });
}

export const pageDefinitionConfig = ({ type, subtype, featureFlags }) => ({
  name: 'new_subtype_index',
  category: 'new_type_index',
  preload: store => preload(store, type, subtype, true),
  preloadAboveFold: featureFlags?.subtypePreloadAboveFold ? store => preload(store, type, subtype) : null,
  component: props => <SubtypeRenderer {...props} type={type} subtype={subtype} />,
  decorator: TypePageDecorator,
  seo: (state, props) => defineSeoContent(state, props, type, subtype),
  options: {
    hasVehicleContext: true,
  },
});

const pages = new Map();

export default params => {
  const { type, subtype, featureFlags } = params;
  const key = `${buildSubTypeRankingsPath(`${type},${subtype}`, subtype)}-${featureFlags?.subtypePreloadAboveFold ? 'ff' : 'no-ff'}`;
  let page = pages.get(key);
  if (!page) {
    page = pageDefinition(pageDefinitionConfig(params));
    pages.set(key, page);
  }
  return page;
};
